import { Button } from '@/components/ui/button';

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  calculateDistance,
  cn,
  formatPreparationTime,
  getTodayStoreOperationStatus,
  hexToRgba,
  isEmpty,
  parseArea,
} from '@/lib/utils';
import { MultiOriginLocation, StoreOrderLocationPickup } from '@/types/http';
import {
  ArrowLeft,
  ClockIcon,
  InfoIcon,
  Loader2,
  MapIcon,
  MapPin,
  Search,
  TruckIcon,
} from 'lucide-react';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { queryClient } from '@/lib/query-client';
import { ThemeType, useBuilderStore } from '@/stores/builder';
import PickupIcon from '@/components/icons/pickup-icon';
import { dummyStoreLocations } from '@/constant/dummyData/dummy-multi-origin-location';
import { Checkbox } from '@/components/ui/checkbox-custom';
import { Label } from '@/components/ui/label';
import Link from 'next/link';
import { useDebounceValue } from '@/hooks/use-debounce';
import JstoreMap, { MapResult } from '@/components/maps-service';
import { relatedProductQueryKeys } from '@/hooks/api/storefront/product';
import { useStoreSetting } from '@/app/(store)/s/[domain]/store-context';

type DrawerSelectLocationProps = {
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  locations: (MultiOriginLocation | StoreOrderLocationPickup)[];
  selectedLocation?: MultiOriginLocation | StoreOrderLocationPickup;
  setSelectedLocation?: Dispatch<
    SetStateAction<(MultiOriginLocation | StoreOrderLocationPickup) | undefined>
  >;
  setDatePickup?: Dispatch<SetStateAction<Date | undefined>>;
  preparationTime?: number;
  distances?: Record<string, number>;
  setDistance?: Dispatch<SetStateAction<Record<string, number>>>;
  theme?: ThemeType;
  preview?: boolean;
  isCheckoutPage?: boolean;
  geoErrorCheckout?: string | null;
  setGeoErrorCheckout?: Dispatch<SetStateAction<string | null>>;
  coordinates?: { lat: number; long: number } | null;
  setCoordinates?: Dispatch<
    SetStateAction<{
      lat: number;
      long: number;
    } | null>
  >;
};

export function DrawerSelectLocation({
  openModal,
  setOpenModal,
  locations,
  selectedLocation,
  setSelectedLocation,
  setDatePickup,
  preparationTime = 0,
  distances = {},
  setDistance,
  theme,
  preview = false,
  isCheckoutPage = false,
  geoErrorCheckout,
  setGeoErrorCheckout,
  coordinates,
  setCoordinates,
}: DrawerSelectLocationProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPickupOnly, setFilterPickupOnly] = useState(false);
  const [distanceStore, setDistanceStore] = useState<Record<string, number>>({});
  const [geoError, setGeoError] = useState<string | null>(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [tab, setTab] = useState<'list_location' | 'set_location'>('list_location');
  const { storeSetting } = useStoreSetting();

  const debounceSearchQuery = useDebounceValue(searchQuery, 500);

  const themeBuilder = useBuilderStore((state) => ({
    theme: state.theme,
  }));
  const themeContent = preview ? themeBuilder.theme : theme;

  const colorScheme = isEmpty(themeContent?.drawer?.colorScheme)
    ? themeContent?.cart?.drawerColorScheme
    : themeContent?.drawer?.colorScheme;

  const sortedLocations = useMemo(() => {
    if (preview) return dummyStoreLocations;

    const withDistance = locations.map((loc) => ({
      ...loc,
      _distance: distanceStore[loc.location_id] ?? Infinity,
    }));

    const sorted = withDistance.sort((a, b) => a._distance - b._distance);
    return sorted;
  }, [locations, distanceStore, preview]);

  const filteredLocations = useMemo(() => {
    const q = debounceSearchQuery.toLowerCase();

    return sortedLocations.filter((loc) => {
      const matchesSearch =
        loc.location_name.toLowerCase().includes(q) ||
        loc.location_address.toLowerCase().includes(q) ||
        parseArea(((loc as MultiOriginLocation | StoreOrderLocationPickup)?.area as string) || '')
          .toLowerCase()
          .includes(q);

      const matchesPickup = filterPickupOnly ? loc.is_pickup_in_store : true;

      return matchesSearch && matchesPickup;
    });
  }, [debounceSearchQuery, sortedLocations, filterPickupOnly]);

  const handleSelectLocation = useCallback(
    (location: MultiOriginLocation | StoreOrderLocationPickup) => {
      if (!isCheckoutPage) {
        Cookies.set('_location_id', location?.location_id ?? '', { expires: 7 });
        queryClient.invalidateQueries({ queryKey: ['product-detail'] });
        queryClient.invalidateQueries({ queryKey: ['collection-list'] });
        queryClient.invalidateQueries({ queryKey: ['carts'] });
        queryClient.invalidateQueries({ queryKey: relatedProductQueryKeys.all });
      }

      if (isCheckoutPage) setDatePickup?.(undefined);
      setSelectedLocation?.(location ?? undefined);
      setOpenModal(false);
    },
    [isCheckoutPage, setDatePickup, setOpenModal, setSelectedLocation]
  );

  const handleSetMyLocation = () => {
    const userLat = coordinates?.lat;
    const userLon = coordinates?.long;

    const newDistances: Record<string, number> = {};
    locations.forEach((loc) => {
      const distance =
        loc.pinpoint?.lat && loc.pinpoint?.lng
          ? calculateDistance(
              userLat as number,
              userLon as number,
              loc.pinpoint.lat,
              loc.pinpoint.lng
            )
          : Infinity;
      newDistances[loc.location_id] = distance;
    });

    setDistanceStore(newDistances);
    if (setDistance) {
      setDistance(newDistances);
    }
    setGeoError(null);
    setGeoErrorCheckout?.(null);
    setGeoLoading(false);

    setTab('list_location');
  };

  const handleOnTriggerMaps = (result: MapResult) => {
    updatePinpoint(result.coordinates.lat, result.coordinates.lng);
  };

  const updatePinpoint = (lat: number, long: number) => {
    if (isValidCoordinate(lat) && isValidCoordinate(long)) {
      setCoordinates?.({ lat, long });
    }
  };

  const isValidCoordinate = (value: number) => {
    return typeof value === 'number' && !isNaN(value);
  };

  useEffect(() => {
    if (!openModal || isCheckoutPage || preview) return;

    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const userLat = pos.coords.latitude;
        const userLon = pos.coords.longitude;

        const newDistances: Record<string, number> = {};
        locations.forEach((loc) => {
          const d =
            loc.pinpoint?.lat && loc.pinpoint?.lng
              ? calculateDistance(userLat, userLon, loc.pinpoint.lat, loc.pinpoint.lng)
              : Infinity;
          newDistances[loc.location_id] = d;
        });

        setCoordinates?.({ lat: userLat, long: userLon });
        setDistanceStore(newDistances);
        setGeoError(null);
        setGeoErrorCheckout?.(null);
        setGeoLoading(false);
      },
      (error) => {
        setGeoLoading(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setGeoError('You denied the location request.');
            break;
          case error.POSITION_UNAVAILABLE:
            setGeoError('Your location is currently unavailable.');
            break;
          case error.TIMEOUT:
            setGeoError('Timed out while trying to get your location.');
            break;
          default:
            setGeoError('Unknown location error occurred.');
        }
      },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    );
  }, [openModal, isCheckoutPage, preview, locations]);

  useEffect(() => {
    if (openModal && !preview && !isCheckoutPage) {
      const id = Cookies.get('_location_id');
      const matched = locations.find((l) => l.location_id === id);
      if (matched) setSelectedLocation?.(matched);

      setSearchQuery('');
      setFilterPickupOnly(false);
      setDistanceStore({});
      setGeoError(null);
      setGeoErrorCheckout?.(null);
    }
  }, [openModal]);

  return (
    <Sheet open={openModal} onOpenChange={setOpenModal}>
      <SheetContent
        side='right'
        className='z-[999] w-full bg-[var(--bg-color)] p-0 sm:max-w-[30rem]'
        style={
          {
            '--text-primary': colorScheme?.text || '#000000',
            '--text-secondary': colorScheme?.textSecondary || '#000000',
            '--bg-color': colorScheme?.background || '#ffffff',
            '--btn-label': colorScheme?.btnSolidlabel || '#ffffff',
            '--btn-bg': colorScheme?.btnSolidBackgroud || '#000000',
          } as React.CSSProperties
        }
      >
        <div className='flex h-full flex-col'>
          <SheetHeader className='border-b p-4'>
            <div className='flex items-center justify-between'>
              <SheetTitle className='flex items-center gap-1 text-xl font-semibold text-[var(--text-primary)]'>
                {tab === 'set_location' && (
                  <ArrowLeft
                    onClick={() => setTab('list_location')}
                    className='size-5 cursor-pointer'
                  />
                )}
                {tab === 'list_location' ? 'List of Store' : 'Set My Location'}
              </SheetTitle>
            </div>
          </SheetHeader>

          {tab === 'list_location' ? (
            <>
              {/* Search and Filter Section */}
              <div className='space-y-4 border-b p-4'>
                {/* Search Input */}
                <div className='relative'>
                  <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400' />
                  <input
                    type='text'
                    placeholder='Search store or address...'
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={cn(
                      'w-full rounded p-2 pl-10 text-xs outline-none ring-1 ring-gray-200 transition-all duration-300 focus:ring-1 focus:ring-[var(--btn-bg)]'
                    )}
                  />
                </div>

                {/* Location Count and Filter */}
                <div className='flex items-center justify-between'>
                  {!isCheckoutPage && storeSetting?.is_pickup_in_store && (
                    <div className='flex items-center'>
                      <Checkbox
                        id='available-for-pickup'
                        checked={filterPickupOnly}
                        onCheckedChange={(checked) => setFilterPickupOnly(!!checked)}
                        className='rounded border-gray-300 text-[var(--btn-bg)] checked:ring-[var(--btn-bg)] focus:ring-1 focus:ring-[var(--btn-bg)] active:ring-1 active:ring-[var(--btn-bg)]'
                      />
                      <Label
                        htmlFor='available-for-pickup'
                        className='ml-2 text-[var(--text-primary)]'
                      >
                        Available for pickup
                      </Label>
                    </div>
                  )}
                  <Button
                    variant='outline'
                    className='p-2 text-xs text-[var(--text-secondary)] hover:text-[var(--text-secondary)] hover:opacity-80'
                    onClick={() => setTab('set_location')}
                    disabled={geoLoading}
                  >
                    {geoLoading ? (
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    ) : (
                      <MapPin className='mr-1 inline h-4 w-4 text-[var(--text-secondary)]' />
                    )}
                    <span>Set My Location</span>
                  </Button>
                </div>
              </div>

              {(geoError || geoErrorCheckout) && (
                <div className='m-4 flex items-center rounded border border-red-500 bg-red-50 p-4'>
                  <InfoIcon className='ml-2 h-4 w-4 text-red-600' />
                  <span className='ml-2 text-sm text-black'>
                    {isCheckoutPage ? geoErrorCheckout : geoError}
                  </span>
                </div>
              )}

              {/* Location List */}
              <div className='flex-1 space-y-2 overflow-y-auto p-2 py-4'>
                {/* Location Count and Filter */}
                <div className='flex items-center justify-between px-3'>
                  <span className='text-sm text-[var(--text-secondary)]'>
                    {filteredLocations.length} Store
                  </span>
                </div>
                {filteredLocations.length > 0 ? (
                  filteredLocations.map((loc, index) => {
                    const isDisabled = (loc.available_stock ?? 0) <= 0 && isCheckoutPage;
                    const isSelected = selectedLocation?.location_id === loc.location_id;
                    const distance = distanceStore[loc.location_id] ?? distances?.[loc.location_id];
                    const kmText =
                      distance && distance !== Infinity
                        ? `± ${(distance / 1000).toFixed(2)} km`
                        : 'unknown distance';
                    const pickupText = !isCheckoutPage
                      ? loc.is_pickup_in_store && 'Pickup in store'
                      : isDisabled
                      ? '- Out of stock'
                      : `- Available pickup in ${formatPreparationTime(preparationTime)}`;
                    const operationStatus = getTodayStoreOperationStatus(
                      loc.location_operations ?? []
                    );
                    const operationTextRaw = operationStatus
                      ? operationStatus.isOpen
                        ? `Open now - Close ${operationStatus.closeTime}`
                        : `Open ${operationStatus.openTime} - Closed ${operationStatus.closeTime}`
                      : 'Closed for today';

                    const operationText =
                      isCheckoutPage || loc.is_pickup_in_store ? operationTextRaw : null;

                    return (
                      <div
                        key={`${loc.location_id}-${index}`}
                        className={cn(
                          'flex cursor-pointer flex-col gap-2 rounded-lg border px-3 py-4 transition-colors',
                          isSelected
                            ? isEmpty(colorScheme)
                              ? 'border-blue-500 bg-blue-50'
                              : ''
                            : 'border-gray-200',
                          isDisabled && 'cursor-not-allowed opacity-50'
                        )}
                        style={
                          isSelected && !isEmpty(colorScheme)
                            ? {
                                borderColor: hexToRgba(colorScheme?.btnSolidBackgroud, 0.5),
                              }
                            : {}
                        }
                        onClick={() => !isDisabled && handleSelectLocation(loc)}
                      >
                        <div className='flex items-center justify-between'>
                          <div className='line-clamp-1 truncate text-sm font-bold'>
                            {loc.location_name}
                          </div>
                          {isSelected && (
                            <span
                              className='rounded-full px-1.5 py-0.5 font-medium'
                              style={{
                                color: colorScheme?.btnSolidBackgroud,
                                backgroundColor: hexToRgba(colorScheme?.btnSolidBackgroud, 0.1),
                              }}
                            >
                              current selection
                            </span>
                          )}
                        </div>
                        <div className='flex items-center justify-between gap-2'>
                          {operationText && storeSetting?.is_pickup_in_store && (
                            <div className='flex items-center gap-2 text-[var(--text-secondary)]'>
                              <ClockIcon className='inline h-[15px] w-[15px] text-[var(--text-secondary)]' />
                              <span>{operationText}</span>
                            </div>
                          )}
                          <div className='text-xs text-gray-500'>{kmText}</div>
                        </div>
                        <div className='flex items-center justify-between gap-2'>
                          <div className='line-clamp-2 flex items-start gap-2 text-xs text-gray-700'>
                            <MapPin className='inline h-4 w-4 text-gray-500' />{' '}
                            {parseArea(
                              (loc as MultiOriginLocation | StoreOrderLocationPickup)?.area || ''
                            )}
                          </div>
                          {storeSetting?.is_pickup_in_store && (
                            <Link
                              href={`https://www.google.com/maps/search/?api=1&query=${loc.pinpoint?.lat},${loc.pinpoint?.lng}`}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='rounded-full p-1 transition-all duration-300 hover:bg-gray-200'
                            >
                              <MapIcon className='h-4 w-4 text-[var(--text-secondary)]' />
                            </Link>
                          )}
                        </div>
                        <div className='flex items-center gap-1 text-xs text-gray-500'>
                          {pickupText && storeSetting?.is_pickup_in_store && (
                            <div>
                              <PickupIcon className='mb-0.5 inline h-4 w-4' />
                            </div>
                          )}
                          <TruckIcon className='inline h-4 w-4 text-gray-500' />
                          {pickupText && storeSetting?.is_pickup_in_store && (
                            <span className='ml-1'>{pickupText}, </span>
                          )}
                          <span>Delivery</span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className='mt-4 w-full text-center text-sm text-[var(--text-secondary)]'>
                    No store found
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className='mt-4 w-full p-4'>
              <JstoreMap
                onResultUpdate={handleOnTriggerMaps}
                coordinates={{
                  lat: coordinates?.lat || 0,
                  lng: coordinates?.long || 0,
                }}
              />
              <Button
                className={cn(
                  'w-full text-xs transition-all duration-300',
                  isEmpty(colorScheme)
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-[var(--btn-bg)] text-[var(--btn-label)] hover:bg-[var(--btn-bg)] hover:opacity-80'
                )}
                onClick={handleSetMyLocation}
                disabled={preview}
              >
                Save My Location
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
